import { useEffect, useRef } from 'react';
import * as Cesium from 'cesium';
import { COVERAGE_REGIONS } from '../data/coverageRegions';
// One on-brand "available" colour used for every region, so the layer reads as a
// single "where LandScope works" surface rather than a rainbow of countries. The
// per-source colour is surfaced only in the hover tooltip swatch (see registry).
const UNIFIED_COLOR = '#37C871';
// Renders the national elevation-data coverage footprints as shaded polygons.
// Regions load once, lazily, on first enable; opacity restyles in place. While
// the layer is visible it also disables the globe's day/night shading so
// coverage stays legible on the night side (e.g. New Zealand in early morning).
export const useDataCoverageOverlay = (viewer, { visible, opacity, onHover }) => {
    const pairsRef = useRef([]);
    const loadStartedRef = useRef(false);
    const disposedRef = useRef(false);
    const opacityRef = useRef(opacity);
    opacityRef.current = opacity;
    const onHoverRef = useRef(onHover);
    onHoverRef.current = onHover;
    const applyOpacity = (ds) => {
        const fill = Cesium.Color.fromCssColorString(UNIFIED_COLOR).withAlpha(opacityRef.current);
        ds.entities.values.forEach((e) => {
            if (e.polygon) {
                e.polygon.material = new Cesium.ColorMaterialProperty(fill);
            }
        });
    };
    // Own the dataSource lifecycle against the current viewer.
    useEffect(() => {
        disposedRef.current = false;
        return () => {
            disposedRef.current = true;
            pairsRef.current.forEach(({ ds }) => {
                try {
                    if (viewer && !viewer.isDestroyed()) {
                        viewer.dataSources.remove(ds, true);
                    }
                }
                catch (_e) {
                    // viewer already torn down — nothing to clean up.
                }
            });
            pairsRef.current = [];
            loadStartedRef.current = false;
        };
    }, [viewer]);
    // Lazy load on first enable. Not cancelled on toggle-off mid-flight, so a quick
    // off/on never leaves coverage partially loaded.
    useEffect(() => {
        if (!viewer || viewer.isDestroyed() || !visible || loadStartedRef.current)
            return;
        loadStartedRef.current = true;
        (async () => {
            for (const region of COVERAGE_REGIONS) {
                if (disposedRef.current || viewer.isDestroyed())
                    return;
                try {
                    const fill = Cesium.Color.fromCssColorString(UNIFIED_COLOR).withAlpha(opacityRef.current);
                    const ds = await Cesium.GeoJsonDataSource.load(region.url, {
                        fill,
                        clampToGround: true,
                    });
                    if (disposedRef.current || viewer.isDestroyed())
                        return;
                    ds.name = `coverage-${region.id}`;
                    ds.show = visible;
                    // Strip GeoJSON name/properties so the global placemark-hover tooltip
                    // (useCesiumEventRouter) ignores these polygons — we drive our own.
                    ds.entities.values.forEach((e) => {
                        e.name = undefined;
                        e.properties = undefined;
                    });
                    await viewer.dataSources.add(ds);
                    if (disposedRef.current || viewer.isDestroyed()) {
                        try {
                            viewer.dataSources.remove(ds, true);
                        }
                        catch (_e) {
                            // viewer torn down between add and this check.
                        }
                        return;
                    }
                    pairsRef.current.push({ ds, region });
                }
                catch (e) {
                    // A region whose footprint isn't populated yet (empty asset) simply
                    // contributes nothing — never block the rest of the overlay.
                    console.warn(`Data coverage overlay: failed to load region "${region.id}"`, e);
                }
            }
        })();
    }, [viewer, visible]);
    // Toggle visibility of already-loaded sources without reloading.
    useEffect(() => {
        pairsRef.current.forEach(({ ds }) => {
            ds.show = visible;
        });
    }, [visible]);
    // Re-apply fill alpha when the opacity slider moves.
    useEffect(() => {
        pairsRef.current.forEach(({ ds }) => applyOpacity(ds));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [opacity]);
    // Coverage supersedes day/night shading: while visible, flatten globe lighting
    // so the night side doesn't hide where data is available. Restore on hide.
    useEffect(() => {
        if (!viewer || viewer.isDestroyed() || !visible)
            return;
        const globe = viewer.scene.globe;
        const prev = globe.enableLighting;
        globe.enableLighting = false;
        return () => {
            if (viewer && !viewer.isDestroyed()) {
                viewer.scene.globe.enableLighting = prev;
            }
        };
    }, [viewer, visible]);
    // Hover: report the region under the cursor for the tooltip. Active only while
    // the overlay is visible.
    useEffect(() => {
        if (!viewer || viewer.isDestroyed() || !visible)
            return;
        const canvas = viewer.scene.canvas;
        const handler = new Cesium.ScreenSpaceEventHandler(canvas);
        handler.setInputAction((movement) => {
            var _a, _b;
            if (viewer.isDestroyed())
                return;
            const picked = viewer.scene.pick(movement.endPosition);
            const ownerCollection = picked && picked.id && picked.id.entityCollection;
            const match = ownerCollection
                ? pairsRef.current.find((p) => p.ds === ownerCollection.owner) || null
                : null;
            if (match) {
                const rect = canvas.getBoundingClientRect();
                (_a = onHoverRef.current) === null || _a === void 0 ? void 0 : _a.call(onHoverRef, {
                    label: match.region.label,
                    x: rect.left + movement.endPosition.x,
                    y: rect.top + movement.endPosition.y,
                });
            }
            else {
                (_b = onHoverRef.current) === null || _b === void 0 ? void 0 : _b.call(onHoverRef, null);
            }
        }, Cesium.ScreenSpaceEventType.MOUSE_MOVE);
        return () => {
            var _a;
            handler.destroy();
            (_a = onHoverRef.current) === null || _a === void 0 ? void 0 : _a.call(onHoverRef, null);
        };
    }, [viewer, visible]);
};
